/**
 * Heading slugs shared by the article renderer and the table of contents.
 *
 * Both sides call `slugifyHeading` on the same source text, so anchors match
 * without threading ids through the markdown pipeline. The function is
 * deliberately stateless — no occurrence counter — which means two identical
 * headings in one document would collide. `docsManifest.test.ts` asserts slug
 * uniqueness per document so that collision is caught at test time instead of
 * silently breaking a TOC link.
 */

export type DocHeadingDepth = 2 | 3;

export type DocHeading = {
    depth: DocHeadingDepth;
    text: string;
    id: string;
};

export const slugifyHeading = (text: string): string => {
    const slug = text
        .toLowerCase()
        .replace(/`/g, "")
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-{2,}/g, "-")
        .replace(/^-|-$/g, "");
    return slug || "section";
};

const FENCE = /^\s*(```|~~~)/;

/**
 * Pull `##` and `###` headings out of a markdown string, skipping anything
 * inside a fenced code block so a commented-out `## foo` in a snippet never
 * lands in the TOC.
 */
export const extractHeadings = (markdown: string): DocHeading[] => {
    const headings: DocHeading[] = [];
    let inFence = false;

    for (const line of markdown.split("\n")) {
        if (FENCE.test(line)) {
            inFence = !inFence;
            continue;
        }
        if (inFence) continue;

        const match = /^(#{2,3})\s+(.*\S)\s*$/.exec(line);
        if (!match) continue;

        const text = match[2].replace(/\s*#+\s*$/, "");
        headings.push({
            depth: match[1].length as DocHeadingDepth,
            text,
            id: slugifyHeading(text),
        });
    }

    return headings;
};

/** Every `/docs/screenshots/<id>.png` referenced by a document. */
export const extractScreenshotIds = (markdown: string): string[] => {
    const ids: string[] = [];
    const pattern = /\/docs\/screenshots\/([a-z0-9-]+)\.png/g;
    let match = pattern.exec(markdown);
    while (match) {
        ids.push(match[1]);
        match = pattern.exec(markdown);
    }
    return ids;
};
